<template>
    <div class="min-max-row" :class="{ vertical: props.vertical }">
        <div class="flex-row-with-gap">
            <label :for="'min-' + id">Min</label>
            <input-number
                :model-value="props.modelValue.min ?? null"
                placeholder="-"
                show-buttons
                :disabled="props.disabled"
                :id="'min-' + id"
                :min="props.min ?? (props.allowNegatives ? undefined : 0)"
                :max="props.modelValue.max ?? undefined"
                :max-fraction-digits="2"
                @update:model-value="updateValue('min', $event)"
            />
        </div>
        <div class="flex-row-with-gap">
            <label :for="'max-' + id">Max</label>
            <input-number
                :model-value="props.modelValue.max ?? null"
                placeholder="-"
                show-buttons
                :disabled="props.disabled"
                :id="'max-' + id"
                :min="props.modelValue.min ?? 0"
                :max="props.max"
                :max-fraction-digits="2"
                @update:model-value="updateValue('max', $event)"
            />
            <p-button v-tooltip="'Reset'" :disabled="props.disabled" @click="reset" text>
                <font-awesome-icon icon="rotate-left" />
            </p-button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { IMinMax } from '@/interfaces/general/IMinMax.ts';
import InputNumber from 'primevue/inputnumber';
import PButton from 'primevue/button';

const id = crypto.randomUUID();

const props = defineProps<{
    modelValue: IMinMax;
    defaultValue?: IMinMax;
    allowNegatives?: boolean;
    disabled?: boolean;
    vertical?: boolean;
    min?: number;
    max?: number;
}>();
const emit = defineEmits<{ 'update:model-value': [IMinMax] }>();

function updateValue (key: keyof IMinMax, value: number | null): void {
    emit('update:model-value', { ...props.modelValue, [key]: value });
}

function reset (): void {
    emit('update:model-value', { min: props.defaultValue?.min ?? null, max: props.defaultValue?.max ?? null });
}

</script>

<style lang="scss" scoped>
.min-max-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--defaultSpacing);
    row-gap: var(--smallSpacing);
    min-width: 0;
    max-width: 100%;

    > .flex-row-with-gap {
        min-width: 0;
    }

    :deep(.p-inputnumber) {
        width: 8.5rem;
        max-width: 100%;
        min-width: 0;
        flex: 0 1 auto;
    }

    :deep(.p-inputnumber-input) {
        width: 100%;
        box-sizing: border-box;
    }

    &.vertical {
        flex-direction: column;
        align-items: flex-start;
    }

    @include responsive('sm') {
        align-items: flex-start;
        flex-direction: column;
    }
}

.p-button {
    font-size: var(--smallFontSize);
}

</style>
